/**
 * Вход в кабинет через Drafter ID (B-1973, SSO).
 *
 * Формы нет — вход/регистрация только на `id.drafter.ru`. При заходе молча
 * обмениваем общую cookie Драфтера (`exchange-drafter`, audience=cabinet) на
 * локальный сеанс:
 * - успех → сохраняем access + lead, уходим в кабинет;
 * - 401 (нет сессии/протухла) → редирект на форму входа `id.drafter.ru`;
 * - 503/сеть → показываем ошибку с повтором.
 *
 * `?deleted=1` — показываем «данные удалены» после права на забвение.
 * `?as=student` — ментор пришёл учиться из админки (B-2378): обмен с
 * `as_student`, бэкенд заводит ему лид, и в админку его не уводим.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  AS_STUDENT_PARAM,
  AS_STUDENT_VALUE,
  exchangeViaCookie,
  redirectToAdmin,
  redirectToDrafterId,
} from '../lib/drafter-auth';
import { tokenStorage } from '../lib/token-storage';

type State = { kind: 'loading' } | { kind: 'error'; message: string };

export function LoginRequiredPage() {
  const [params] = useSearchParams();
  const deleted = params.get('deleted') === '1';
  const asStudent = params.get(AS_STUDENT_PARAM) === AS_STUDENT_VALUE;
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (deleted) return;
    let cancelled = false;
    (async () => {
      try {
        const { accessToken, leadId, user } = await exchangeViaCookie({ asStudent });
        if (cancelled) return;
        // B-2175: ментор, который не учится, — не студент. Уводим его в админку
        // ДО сохранения кабинетного сеанса и стены привязки Telegram.
        // B-2378: ментор может быть и учеником. Признак ученика — лид: он есть
        // у ментора-участника и заводится при входе с `?as=student`. Поэтому
        // уводим только ментора без лида — иначе повторный вход после
        // истечения access (без параметра) выкидывал бы ментора-ученика.
        // `?.` намеренно: ответ без `user` (рассинхрон деплоя, HTML от прокси)
        // не должен ронять вход TypeError'ом — обычный вход в кабинет важнее
        // бунса, а решать «ментор ли он» без данных всё равно не по чему.
        if (user?.is_mentor && !leadId) {
          redirectToAdmin();
          return;
        }
        tokenStorage.save({ accessToken, leadId });
        // Полный reload — react-query сбросит кеш прошлой сессии.
        window.location.replace('/');
      } catch (error) {
        if (cancelled) return;
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401) {
          // Нет валидной сессии Драфтера → на форму входа IdP.
          redirectToDrafterId();
          return;
        }
        setState({
          kind: 'error',
          message:
            status === 503
              ? 'Сервис авторизации временно недоступен.'
              : 'Не удалось войти. Попробуй ещё раз через минуту.',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deleted, asStudent]);

  if (deleted) {
    return (
      <main className="flex min-h-[100dvh] items-center justify-center px-4 py-10">
        <div className="w-full max-w-md text-center">
          <p className="mb-3 text-3xl">👋</p>
          <h1 className="mb-4 text-2xl font-semibold leading-tight text-slate-900 dark:text-slate-100 sm:text-3xl">
            Данные удалены
          </h1>
          <p className="mb-8 text-base text-slate-600 dark:text-slate-300">
            Твои данные удалены из системы. Чтобы вернуться — войди снова через Drafter ID.
          </p>
          <button
            type="button"
            onClick={redirectToDrafterId}
            className="inline-block rounded-xl bg-brand-600 px-6 py-3 text-base font-medium text-white shadow-sm transition active:scale-[0.98]"
          >
            Войти
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-[100dvh] items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm text-center">
        {state.kind === 'loading' && (
          <>
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-brand-100 border-t-brand-600" />
            <p className="text-base text-slate-600 dark:text-slate-300">Входим через Drafter ID…</p>
          </>
        )}
        {state.kind === 'error' && (
          <>
            <p className="mb-3 text-2xl">😕</p>
            <h1 className="mb-3 text-xl font-semibold text-slate-900 dark:text-slate-100">
              Не получилось
            </h1>
            <p className="mb-6 text-base text-slate-600 dark:text-slate-300">{state.message}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-block rounded-xl bg-brand-600 px-5 py-3 text-base font-medium text-white shadow-sm"
            >
              Повторить
            </button>
          </>
        )}
      </div>
    </main>
  );
}
